import { ComponentUtils } from '@Components/utils'

import './selector.scss'

export type Selectable<T> = {
  label: string
  value: T
}

/**
 * Where the select's accessible name comes from. Exactly one source: a visible label element by
 * id, or a string when there is no such element. Both at once is not a preference the accessible
 * name computation honours — `aria-labelledby` wins and the string is discarded — so the type
 * forbids it rather than a comment explaining it.
 */
type SelectorName =
  | { ariaLabel: string; ariaLabelledBy?: never }
  | { ariaLabelledBy: string; ariaLabel?: never }

export type SelectorProps<T> = SelectorName & {
  placeHolderText: string
  selectables: Selectable<T>[]
  onValueChange: (val: T) => void
  autoComplete?: string
  className?: string
  value?: string
  required?: boolean
  disabled?: boolean
}

const CLASS_NAME = 'selector'

export const Selector = <T,>({
  ariaLabel,
  placeHolderText,
  selectables,
  onValueChange,
  ariaLabelledBy,
  autoComplete,
  className,
  value,
  required,
  disabled,
}: SelectorProps<T>) => {
  const classNames: string[] = [CLASS_NAME]
  if (className) {
    classNames.push(className)
  }

  // Options carry the stringified value, so the selection is matched back to its selectable to
  // recover the original type rather than handing the caller a string it did not ask for.
  const valueChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = selectables.find((selectable) => `${selectable.value}` === event.target.value)

    if (selected) {
      onValueChange(selected.value)
    }
  }

  return (
    <select
      className={ComponentUtils.mapClassPrefixesWithSuffix(classNames, '-input')}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      autoComplete={autoComplete}
      onChange={valueChangeHandler}
      value={value ?? placeHolderText}
      required={required}
      disabled={disabled}>
      <option value={placeHolderText} disabled hidden>
        {placeHolderText}
      </option>

      {selectables.map((s) => {
        return (
          <option key={`option-${s.label}`} value={`${s.value}`}>
            {s.label}
          </option>
        )
      })}
    </select>
  )
}
